/* DC About Block loader (block 6). The lightest of the block loaders: one reveal class
   when the section enters view, and, only when a scene URL is set, the same lazy
   self-hosted Spline runtime path the sponsors and tickets blocks use (WebGPU adapter
   gate, poster stays on WebGL devices, play and stop with visibility, runtime released
   a viewport away on touch devices, safe re-init in the theme editor). No pin, no
   scroll-linked motion: the reference this block follows moves only on hover, and the
   scene carries that itself. */
(() => {
  const MOBILE = '(max-width: 767px)';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches && !window.__dcForceMotion;
  const saveData = navigator.connection && navigator.connection.saveData;
  const RELEASE_MS = 1500;

  const gpuReady = async () => {
    try { return !!(navigator.gpu && await navigator.gpu.requestAdapter()); } catch (e) { return false; }
  };
  const touchDevice = () => matchMedia('(pointer: coarse)').matches || (navigator.maxTouchPoints || 0) > 0;

  /* Reveal: flips once when 18 percent of the block is on screen; without JS the CSS
     shows everything in place. */
  const reveal = (root) => {
    root.classList.add('has-js');
    if (reduced || !('IntersectionObserver' in window)) { root.classList.add('is-in'); return; }
    const io = new IntersectionObserver((entries, obs) => {
      if (entries.some((en) => en.isIntersecting)) { root.classList.add('is-in'); obs.disconnect(); }
    }, { threshold: 0.18 });
    io.observe(root);
  };

  const teardown = (root) => {
    try { root.__dcIO && root.__dcIO.disconnect(); } catch (e) {}
    try { root.__dcFar && root.__dcFar.disconnect(); } catch (e) {}
    try { root.__dcSpinStop && root.__dcSpinStop(); } catch (e) {}
    try { root.__dcApp && root.__dcApp.dispose(); } catch (e) {}
    if (root.__dcOnResize) { window.removeEventListener('resize', root.__dcOnResize); root.__dcOnResize = null; }
    root.__dcIO = null; root.__dcFar = null; root.__dcApp = null;
    root.classList.remove('is-live');
  };

  /* Touch devices: once the block is a full viewport away for RELEASE_MS, give the runtime
     back (WebKit charges every resident scene against the page) and return to the poster
     and the lazy watch, which re-boots from cache when it comes near again. */
  const releaseWhenFar = (root, still) => {
    if (!touchDevice() || !('IntersectionObserver' in window)) return null;
    let timer = 0;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { clearTimeout(timer); timer = 0; return; }
        if (timer) return;
        timer = setTimeout(() => {
          timer = 0;
          if (!still()) return;
          teardown(root);
          if (root.__dcWatch) root.__dcWatch();
        }, RELEASE_MS);
      });
    }, { rootMargin: '100% 0px 100% 0px', threshold: 0 });
    io.observe(root);
    return io;
  };

  const boot = async (root) => {
    if (root.__dcApp) return;
    const canvas = root.querySelector('[data-dc-canvas]');
    if (!canvas || !root.dataset.scene) return;
    if (!(await gpuReady())) { root.classList.add('is-static'); return; }
    const { Application } = await import(root.dataset.runtime);
    const app = new Application(canvas);
    root.__dcApp = app;
    await app.load(root.dataset.scene);
    try { app.setBackgroundColor('#070707'); } catch (e) {}
    /* The export carries a fixed frame (886 x 446 at the time of writing), and the runtime
       sizes the canvas to it and then never refits, so the scene drew into the top left of
       the stage. Size it to the stage here and again on resize; the CSS pins the canvas to
       100 percent so the runtime's inline size cannot shrink it back. */
    const fit = () => {
      try {
        const r = canvas.parentElement.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) app.setSize(Math.round(r.width), Math.round(r.height));
      } catch (e) {}
    };
    fit();
    let fitTimer = 0;
    const onResize = () => { clearTimeout(fitTimer); fitTimer = setTimeout(fit, 150); };
    window.addEventListener('resize', onResize, { passive: true });
    root.__dcOnResize = onResize;
    /* After load the runtime keeps compiling materials for seconds (600 objects: measured,
       the car's front drew alone for two seconds and the full 395k triangles only arrived
       at 4.9 s). The renderer's per-frame triangle count climbs as pipelines land and then
       holds, so the poster stays up until that count has been steady for 45 frames (with a
       1 s floor and a 10 s ceiling), and only then cross-fades. */
    await new Promise((resolve) => {
      const t0 = performance.now(); let last = -1, steady = 0;
      const tick = () => {
        const el = performance.now() - t0;
        let tris = null; try { tris = app._renderer.info.render.triangles; } catch (e) {}
        if (tris === null) { if (el > 4000) return resolve(); }
        else { if (tris > 0 && tris === last) steady += 1; else steady = 0; last = tris; if (el > 1000 && steady >= 45) return resolve(); }
        if (el > 10000) return resolve();
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
    if (root.__dcApp !== app) return;
    /* Fixed distance. The export ships with orbit controls, so a wheel over the canvas
       zoomed the camera and every visitor got a different framing. The authored pose is
       captured here, once settled, and written back right before every render, which
       beats the controls whatever they do. Only the car turns. */
    try {
      const cam = app._camera;
      const pose = { p: cam.position.clone(), r: cam.rotation.clone(), z: cam.zoom, f: cam.fov };
      const renderer = app._renderer;
      if (renderer && !renderer.__dcHeld) {
        const orig = renderer.render.bind(renderer);
        renderer.__dcHeld = true;
        renderer.render = function (scene, camera) {
          if (camera && camera === app._camera) {
            camera.position.copy(pose.p); camera.rotation.copy(pose.r);
            if (camera.zoom !== pose.z || camera.fov !== pose.f) { camera.zoom = pose.z; camera.fov = pose.f; camera.updateProjectionMatrix && camera.updateProjectionMatrix(); }
            if (camera.matrixAutoUpdate === false) camera.updateMatrix();
            camera.matrixWorldNeedsUpdate = true;
          }
          return orig(scene, camera);
        };
      }
    } catch (e) {}
    /* The wheel over the canvas used to be eaten by the runtime (it zoomed, now it does
       nothing), so the page could not scroll through this block with the pointer on the
       stage. Take the wheel before the runtime sees it and hand it to the page: Lenis when
       it runs (it listens on window), native scroll otherwise. */
    if (!canvas.__dcWheel) {
      canvas.__dcWheel = (e) => {
        e.stopImmediatePropagation();
        const api = window.__dcEvent;
        if (api && api.lenis) {
          e.preventDefault();
          try { window.dispatchEvent(new WheelEvent('wheel', { deltaX: e.deltaX, deltaY: e.deltaY, deltaMode: e.deltaMode, clientX: e.clientX, clientY: e.clientY, bubbles: true, cancelable: true })); } catch (e2) {}
        }
      };
      canvas.addEventListener('wheel', canvas.__dcWheel, { capture: true, passive: false });
    }
    root.classList.add('is-live');
    /* The turntable. Driven from here rather than the file so it needs no re-export: the
       car group turns once every SPIN_S seconds while the block is on screen, and the
       scene's own hover transition keeps turning the disc and rings under it. Stops with
       visibility and honours reduced motion (the scene never boots under it anyway). */
    const SPIN_S = 26;
    let spinObj = null;
    try { spinObj = app.findObjectByName(root.dataset.spinObject || '2001__bmw_m3_gtr_e46__nfsmw') || null; } catch (e) {}
    let spinRaf = 0, spinLast = 0;
    const spinTick = (t) => {
      spinRaf = 0;
      if (!root.__dcVisible || root.__dcApp !== app || !spinObj) return;
      if (spinLast) spinObj.rotation.y += ((t - spinLast) / 1000) * (Math.PI * 2 / SPIN_S);
      spinLast = t;
      spinRaf = requestAnimationFrame(spinTick);
    };
    const spinStart = () => { if (!spinRaf && spinObj) { spinLast = 0; spinRaf = requestAnimationFrame(spinTick); } };
    const spinStop = () => { if (spinRaf) cancelAnimationFrame(spinRaf); spinRaf = 0; };
    root.__dcSpinStop = spinStop;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        try {
          root.__dcVisible = en.isIntersecting;
          if (en.isIntersecting) { app.play(); spinStart(); } else { app.stop(); spinStop(); }
        } catch (e) {}
      });
    }, { threshold: 0.01 });
    /* Booted off screen: park it now that it is compiled; the observer wakes it. */
    try { if (!root.__dcVisible) app.stop(); } catch (e) {}
    io.observe(root);
    root.__dcIO = io;
    root.__dcFar = releaseWhenFar(root, () => root.__dcApp === app);
  };

  const watchScene = (root) => {
    if (!root.dataset.scene || reduced || saveData || !('IntersectionObserver' in window)) return;
    /* The stage export carries the car's baked textures (tens of MB); a phone keeps the poster. */
    if (root.dataset.desktopOnly === 'true' && (touchDevice() || matchMedia(MOBILE).matches)) return;
    const margin = parseInt(root.dataset.rootMargin || '400', 10) || 400;
    const go = () => boot(root).catch(() => root.classList.remove('is-live'));
    const watch = () => {
      const lazy = new IntersectionObserver((entries, obs) => {
        if (entries.some((en) => en.isIntersecting)) { obs.disconnect(); go(); }
      }, { rootMargin: margin + 'px 0px' });
      lazy.observe(root);
    };
    root.__dcWatch = watch;
    /* Desktop pointers keep their runtimes, so this scene (31 MB, seconds of material
       compile) boots in the BACKGROUND once the hero has armed and the page has had a
       breath, with the canvas hidden by CSS until it has settled. By the time a visitor
       scrolls down to it, it is drawn. Touch devices never get here (desktop_only) and a
       desktop that somehow does not fire the idle path still has the lazy watch. */
    if (!touchDevice()) {
      /* Warm the HTTP cache for the 31 MB scene from the moment the page lands, at low
         priority so the hero's own scene wins the first seconds. Spline's server sends an
         ETag and no cache-control, so the runtime's later request revalidates to a 304 and
         reads the body from cache instead of downloading it again. */
      try { fetch(root.dataset.scene, { mode: 'cors', priority: 'low', credentials: 'omit' }).catch(() => {}); } catch (e) {}
      let done = false;
      const early = () => { if (done) return; done = true; setTimeout(go, 500); };
      const hero = document.querySelector('.dc-hero');
      if (hero && !(hero.__dcHero && hero.__dcHero.armed) && !hero.classList.contains('is-motion')) {
        document.addEventListener('dc:hero:armed', early, { once: true });
        setTimeout(early, 6000);
      } else setTimeout(early, 1500);
    }
    watch();
    /* A breakpoint change re-boots so the scene's own mobile state can apply. */
    let wasMobile = matchMedia(MOBILE).matches;
    matchMedia(MOBILE).addEventListener('change', (e) => {
      if (e.matches === wasMobile) return;
      wasMobile = e.matches;
      if (root.__dcApp) { teardown(root); watch(); }
    });
  };

  const init = (root) => {
    if (root.__dcInit) return;
    root.__dcInit = true;
    reveal(root);
    watchScene(root);
  };

  const all = () => document.querySelectorAll('[data-dc-about]');
  const start = () => all().forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-dc-about]').forEach(init));
    document.addEventListener('shopify:section:unload', (e) => e.target.querySelectorAll('[data-dc-about]').forEach(teardown));
  }
  window.addEventListener('pagehide', () => all().forEach(teardown));
})();
